import React, { FunctionComponent, useState } from 'react';
import { cloneDeep, set, get } from 'lodash';
import { AppointmentType, BusinessHoursType, DAYS } from './AppointmentList';

interface Props {
  enabled: boolean;
  day: (typeof DAYS)[number];
  scheduleConfig: any;
  onChange: Function;
  setScheduleConfig: Function;
  defaultDayConfig: {
    enabled: boolean;
    start: number;
    end: number;
  };
  scheduleType: AppointmentType | BusinessHoursType;
}

const getDaysOptions = () => {
  const daysOptions = [];

  for (let i = 1; i < 24; i += 1) {
    const isPM = i >= 12;
    const displayValue = i > 12 ? i - 12 : i;
    daysOptions.push(
      <option value={i} key={i}>
        {displayValue}:00 {isPM ? 'PM' : 'AM'}
      </option>,
    );
  }

  return daysOptions;
};

export const HoursSelection: FunctionComponent<Props> = ({
  enabled,
  day,
  scheduleConfig,
  onChange,
  setScheduleConfig,
  defaultDayConfig,
  scheduleType,
}) => {
  const [invalidHour, setInvalidHour] = useState(false as boolean);

  const isInvalidRange = (rangePoint: string, value: string) => {
    const start = scheduleConfig.days[day].start || defaultDayConfig.start;
    const end = scheduleConfig.days[day].end || defaultDayConfig.end;
    if (rangePoint === 'start' && end <= value) {
      return true;
    }

    return rangePoint === 'end' && start >= value;
  };

  const handleChangeTime = (rangePoint: string, value: string) => {
    if (isInvalidRange(rangePoint, value)) {
      setInvalidHour(true);
      return;
    }
    setInvalidHour(false);

    const scheduleConfigCopy = cloneDeep(scheduleConfig);
    set(scheduleConfigCopy, `days.${day}.${rangePoint}`, parseInt(value, 10));
    setScheduleConfig(scheduleConfigCopy);
    onChange(scheduleConfigCopy);
  };

  const handleToggleDay = (value: boolean) => {
    const scheduleConfigCopy = cloneDeep(scheduleConfig);
    // if the start or end is undefined, we will need to use default value
    const start = get(
      scheduleConfigCopy,
      `days.${day}.start`,
      defaultDayConfig.start,
    );
    const end = get(
      scheduleConfigCopy,
      `days.${day}.end`,
      defaultDayConfig.end,
    );
    const updatedDayObj = {
      start,
      end,
      enabled: value,
    };
    set(scheduleConfigCopy, `days.${day}`, updatedDayObj);
    setScheduleConfig(scheduleConfigCopy);
    onChange(scheduleConfigCopy);
  };

  return (
    <div
      className="input-container date-range-input-group"
      key={`input-group-${day}`}
    >
      <input
        type="checkbox"
        name={`${scheduleType}-${day}-enable`}
        className={'date-range-checkbox'}
        checked={enabled}
        onChange={() => {
          handleToggleDay(!enabled);
        }}
      />
      <label
        className="date-range-label"
        htmlFor={`${scheduleType}-${day}-enable`}
      >
        {day}
      </label>
      <select
        name={`${scheduleType}-${day}-start`}
        onChange={(e) => handleChangeTime('start', e.target.value)}
        disabled={!enabled}
        className={invalidHour ? 'invalid' : ''}
        defaultValue={
          get(scheduleConfig, `days.${day}.start`) || defaultDayConfig.start
        }
      >
        {getDaysOptions()}
      </select>
      <span className="date-range-spacer-label">To</span>
      <select
        name={`${scheduleType}-${day}-end`}
        onChange={(e) => handleChangeTime('end', e.target.value)}
        disabled={!enabled}
        className={invalidHour ? 'invalid' : ''}
        defaultValue={
          get(scheduleConfig, `days.${day}.end`) || defaultDayConfig.end
        }
      >
        {getDaysOptions()}
      </select>
    </div>
  );
};
